/* eslint-disable max-lines -- cohesive set of composer add-menu submenu primitives (context picker, sources, skills) that share one popover/flyout contract. */

import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandList,
} from '@heidi/design-system/command';
import { FramedIcon, type FramedIconElement } from '@heidi/design-system/framed-icon';
import { Routines } from '@heidi/design-system/icons';
import { Kbd } from '@heidi/design-system/kbd';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { PopoverMenuItem } from '@heidi/design-system/popover-menu-item';
import { focusRing } from '@heidi/design-system/styles';
import {
  TypographyCaption,
  TypographyP2,
  typographyVariant,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { BookOpen, Check, ChevronRight, Gauge, SlashSquare } from 'lucide-react';
import {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import type { WorkflowSkill } from '../../../contracts';
import {
  linkedContextItemKey,
  type LinkedContextItem,
} from '../../../contracts/projects/linked-context';

export const ADD_MENU_ROW_CLASSNAME = cn('group/add-menu-row gap-2.5', focusRing);
export const ADD_MENU_FRAMED_ICON_CLASSNAME = cn(
  '[&_svg]:text-text-secondary',
  // Track what the row bg tracks (hover, open flyout), never focus: the Sources
  // trigger regains focus when its panel closes, which would darken the frame
  // while the row (no focus bg) stays resting.
  'group-hover/add-menu-row:bg-background-secondary-hover',
  'newBrandDark:group-hover/add-menu-row:bg-background-secondary-hover',
  'group-data-[submenu-open]/add-menu-row:bg-background-secondary-hover',
  'newBrandDark:group-data-[submenu-open]/add-menu-row:bg-background-secondary-hover'
);

export interface ComposerMenuRowProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'children' | 'title' | 'type'
> {
  icon: ReactNode;
  title: ReactNode;
  secondary?: ReactNode;
  selected?: boolean;
}

export const ComposerMenuRow = forwardRef<HTMLButtonElement, ComposerMenuRowProps>(
  ({ icon, title, secondary, selected, className, ...buttonProps }, ref) => (
    <button
      {...buttonProps}
      ref={ref}
      type="button"
      data-selected={selected ? 'true' : undefined}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center gap-3 rounded-lg px-3 py-2 text-start text-text-primary outline-none',
        selected === undefined
          ? 'hover:bg-surface-hover'
          : 'data-[selected=true]:bg-surface-hover',
        className
      )}
    >
      {icon}
      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(typographyVariant({ variant: 'p2' }), 'truncate font-normal')}
        >
          {title}
        </span>
        {secondary ? (
          <span
            className={cn(
              typographyVariant({ variant: 'caption' }),
              'truncate font-normal text-text-tertiary'
            )}
          >
            {secondary}
          </span>
        ) : null}
      </span>
    </button>
  )
);

ComposerMenuRow.displayName = 'ComposerMenuRow';

const CONTEXT_SUBMENU_COLLISION_PADDING = 8;
const CONTEXT_SUBMENU_SIDE_OFFSET = 4;
const CONTEXT_SUBMENU_PANE_WIDTH = 320;
// Taller than the old 18rem cap, still clamped to the space Radix measures.
// The list subtracts the search row (h-9 plus a little slack) so the
// pane's overflow-hidden clips the radius, not the last row.
const CONTEXT_SUBMENU_PANE_MAX_HEIGHT =
  'max-h-[min(32rem,var(--radix-popover-content-available-height))]';
const CONTEXT_SUBMENU_LIST_MAX_HEIGHT =
  'max-h-[min(29.25rem,calc(var(--radix-popover-content-available-height)-2.75rem))]';

type FlyoutSide = 'left' | 'right';

const resolveFlyoutSide = (
  rect: Pick<DOMRect, 'left' | 'right' | 'width'>,
  viewportWidth: number,
  {
    paneWidth,
    sideOffset,
    collisionPadding,
  }: { paneWidth: number; sideOffset: number; collisionPadding: number }
): FlyoutSide => {
  const needed = sideOffset + paneWidth + collisionPadding;
  if (viewportWidth <= 0 || rect.width === 0) return 'right';
  const rightRoom = viewportWidth - rect.right;
  const leftRoom = rect.left;
  if (rightRoom >= needed) return 'right';
  if (leftRoom >= needed) return 'left';
  return leftRoom > rightRoom ? 'left' : 'right';
};

// Same viewport inset as ContextSubmenu's, but intentionally not shared: a
// cross-component re-export was flagged on PR 1 and its test only worked by
// comparing the prop to the very constant it mirrored. Each flyout keeps its
// own literal here.
const SPEED_SUBMENU_COLLISION_PADDING = 8;

interface ContextSubmenuProps {
  icon: FramedIconElement;
  label: string;
  shortcutHint?: string;
  linkedName?: string;
  isActive: boolean;
  searchPlaceholder: string;
  emptyMessage: string;
  isEmpty: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  search: string;
  onSearchChange: (value: string) => void;
  children: JSX.Element;
}

export const ContextSubmenu = ({
  icon,
  label,
  shortcutHint,
  linkedName,
  isActive,
  searchPlaceholder,
  emptyMessage,
  isEmpty,
  open,
  onOpenChange,
  search,
  onSearchChange,
  children,
}: ContextSubmenuProps): JSX.Element => {
  // Row hover/focus/click only opens. Close is the parent's one-open
  // swap (sibling row) or the add-menu dismissing — not mouseleave.
  // Filtering used to shrink this pane off the pointer, fire leave,
  // and clear the query on remount.
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [side, setSide] = useState<FlyoutSide>('right');

  useLayoutEffect(() => {
    if (!open) {
      setSide('right');
      return;
    }
    const anchor = anchorRef.current;
    if (!anchor) return;
    setSide(
      resolveFlyoutSide(
        anchor.getBoundingClientRect(),
        document.documentElement.clientWidth,
        {
          paneWidth: CONTEXT_SUBMENU_PANE_WIDTH,
          sideOffset: CONTEXT_SUBMENU_SIDE_OFFSET,
          collisionPadding: CONTEXT_SUBMENU_COLLISION_PADDING,
        }
      )
    );
  }, [open]);

  const openSubmenu = useCallback(() => {
    onOpenChange(true);
  }, [onOpenChange]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <PopoverMenuItem
          ref={anchorRef}
          onMouseEnter={openSubmenu}
          onFocus={openSubmenu}
          onClick={openSubmenu}
          className={cn(
            ADD_MENU_ROW_CLASSNAME,
            !linkedName && 'items-center',
            open && 'bg-surface-hover'
          )}
          selected={isActive}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={label}
          data-submenu-open={open || undefined}
          icon={
            <FramedIcon
              icon={icon}
              size="sm"
              className={ADD_MENU_FRAMED_ICON_CLASSNAME}
            />
          }
          description={linkedName}
          accessoryEnd={
            <span className="flex items-center gap-1">
              {shortcutHint ? (
                <Kbd size="compact" aria-hidden>
                  {shortcutHint}
                </Kbd>
              ) : null}
              <ChevronRight className="size-4 shrink-0 text-text-secondary rtl:-scale-x-100" />
            </span>
          }
        >
          {label}
        </PopoverMenuItem>
      </PopoverAnchor>
      <PopoverContent
        // EVI-2553: measured once per open and frozen while open, so the pane
        // never moves under the pointer; left only when the right has no room.
        side={side}
        align="start"
        sideOffset={CONTEXT_SUBMENU_SIDE_OFFSET}
        // Collision avoidance would flip the pane to `left-start`, teleporting
        // it across the anchor row and the pointer (measured: x=1374 → x=826 at
        // 1440x800). Radix runs `size()` outside the `avoidCollisions` guard,
        // so `--radix-popover-content-available-height` is still published and
        // the pane shrinks to fit instead — the AGE-1125 contract.
        avoidCollisions={false}
        // `size()` reads this padding even with collisions off: it is the
        // viewport inset the available-height measurement leaves clear.
        collisionPadding={CONTEXT_SUBMENU_COLLISION_PADDING}
        // Hover flyout: default popover zoom scales the hit box. Pointing at
        // the search (top edge) then shrinks the pane off the cursor.
        className={cn(
          'flex w-80 flex-col overflow-hidden rounded-lg p-0 data-[state=closed]:animate-none data-[state=open]:animate-none',
          CONTEXT_SUBMENU_PANE_MAX_HEIGHT
        )}
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Closing must not restore focus onto this row or the next one.
        onCloseAutoFocus={(e) => e.preventDefault()}
        // Hover-driven flyout: focus moving elsewhere must not dismiss it.
        // The Sources panel is the concrete case — it takes focus while open,
        // and Radix returns focus to its trigger when it closes; without this
        // guard that focus blip lands "outside" a freshly opened sibling
        // flyout and dismisses it (sources → patients/routines felt dead).
        // Sibling hover and the add-menu dismissing remain the only closers.
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        onMouseEnter={openSubmenu}
      >
        <Command
          shouldFilter={false}
          // `size-full` (h-full) on an auto-height pane doesn't resolve, so the
          // command hugs its content and the pane cap can shrink it.
          className="h-auto max-h-full min-h-0 w-full rounded-lg border-none"
        >
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={onSearchChange}
            showClearButton
            wrapperClassName="flex w-full min-w-0 shrink-0"
          />
          {/*
            One scrollport. `flex-auto` (basis: auto) keeps a short list at its
            content height; `min-h-0` lets the pane max-height shrink a long one
            into this scroller. The max-height is the pane cap minus the search
            row, so overflow-hidden on the pane never slices the last row.
            `scrollbar-subtle` per scrollbars.md: a scroller inside an
            `overflow-hidden` rounded parent needs the flush thumb.
            Padding lives inside the scroller so the last row clears the radius.
          */}
          <CommandList
            className={cn(
              'scrollbar-subtle min-h-0 flex-auto overflow-y-auto',
              CONTEXT_SUBMENU_LIST_MAX_HEIGHT
            )}
          >
            {isEmpty ? (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            ) : (
              <div className="p-1">{children}</div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface PickerListProps {
  items: readonly LinkedContextItem[];
  linkedKeys: ReadonlySet<string>;
  onPick: (item: LinkedContextItem) => void;
  icon: JSX.Element;
  renderLabel: (item: LinkedContextItem) => string;
  renderSub?: (item: LinkedContextItem) => string | undefined;
}

export const PickerList = ({
  items,
  linkedKeys,
  onPick,
  icon,
  renderLabel,
  renderSub,
}: PickerListProps): JSX.Element => (
  <>
    {items.map((item) => {
      const key = linkedContextItemKey(item);
      const isActive = linkedKeys.has(key);
      const sub = renderSub?.(item);
      return (
        <button
          key={key}
          type="button"
          onClick={() => onPick(item)}
          className="flex w-full flex-col items-start gap-0.5 rounded-md p-2 text-start hover:bg-surface-hover"
        >
          <div className="flex w-full items-center gap-2">
            {icon}
            <TypographyP2 className="flex-1 truncate text-text-primary">
              {renderLabel(item)}
            </TypographyP2>
            {isActive ? <Check size={14} className="shrink-0 text-text-primary" /> : null}
          </div>
          {sub ? (
            <TypographyCaption className="ps-5 text-text-tertiary">
              {sub}
            </TypographyCaption>
          ) : null}
        </button>
      );
    })}
  </>
);

/**
 * "Sources" submenu — reuses the Evidence source-control panel wholesale so
 * the flyout shows the exact same toggles (Clinical web / Literature and
 * guidelines), the My library and Partner sources submenus, and the upgrade
 * footer the user already configures in Evidence.
 *
 * Opens to the right as the second panel on hover and collapses shortly after
 * the pointer leaves the area — consistent with the other rows. "The area"
 * here spans the trigger, the panel, AND the panel's own nested flyouts (My
 * library / Partner sources), which portal out as separate right-side
 * popovers. Because those siblings escape the panel's DOM subtree we can't
 * rely on a single mouse-leave; instead, while open, a document-level pointer
 * watch keeps the panel open whenever the cursor is over any popover flyout
 * that isn't the parent add-menu (tagged ``data-add-menu``), and schedules a
 * close once it leaves all of them.
 */
const SOURCES_CLOSE_DELAY_MS = 200;

/**
 * The panel the Sources row opens, supplied by the host.
 *
 * The panel itself is the Evidence source-control popover, which lives inside
 * Chat: it reads the source-control atoms, the Evidence billing tier, the team
 * enforcement rules and four feature flags, and it renders its own trigger
 * wrapper around whatever child it is given. None of that can come behind a
 * package door, and RE-HOMING it would not help — this row would still be naming
 * an app module. So the panel crosses as a render prop and this component keeps
 * what is actually its own: the row, the hover grace, and the pointer watch that
 * decides when the flyout closes.
 *
 * `children` is the Sources ROW — the popover's anchor and its only child. The
 * host binds placement (`side`/`align`), because where the flyout sits relative
 * to the add-menu is the composing surface's decision, not this row's.
 */
export interface SourcesPopoverProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The Sources ROW — the popover's anchor and its only child. */
  readonly children: ReactNode;
}

/**
 * A COMPONENT type, not a render function, and the difference is load-bearing in
 * two places. React-side, the panel gets its own element and therefore its own
 * hook scope rather than running its (many) hooks inside this row's render.
 * Repo-side, the app module that binds it exports a PascalCase component instead
 * of a lower-camel function, so the binding module stays a components-only module
 * — `react-refresh/only-export-components` is ratcheted at zero and a mixed module
 * is what trips it.
 */
export type SourcesPopoverComponent = (props: SourcesPopoverProps) => JSX.Element;

export const SourcesSubmenu = ({
  open,
  onOpenChange,
  SourcesPopover,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  SourcesPopover: SourcesPopoverComponent;
}): JSX.Element => {
  const intl = useIntl();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const label = intl.formatMessage({
    defaultMessage: 'Sources',
    id: 's08AbiuxkF',
    description: "Submenu label opening the user's Evidence source controls",
  });

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => onOpenChange(false), SOURCES_CLOSE_DELAY_MS);
  }, [cancelClose, onOpenChange]);

  // True when the pointer is over the Sources trigger or any of the panel's
  // own flyouts. The panel and its nested submenus each render in their own
  // popover wrapper; the parent add-menu carries ``data-add-menu`` so we can
  // exclude it (moving back onto a sibling row should let Sources close).
  const isInSourcesRegion = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    if (triggerRef.current?.contains(target)) return true;
    const flyout = target.closest('[data-radix-popper-content-wrapper]');
    return !!flyout && !flyout.querySelector('[data-add-menu]');
  }, []);

  useEffect(() => {
    if (!open) {
      cancelClose();
      return;
    }
    const handlePointerOver = (event: PointerEvent) => {
      if (isInSourcesRegion(event.target)) {
        cancelClose();
      } else {
        scheduleClose();
      }
    };
    document.addEventListener('pointerover', handlePointerOver);
    return () => {
      document.removeEventListener('pointerover', handlePointerOver);
      cancelClose();
    };
  }, [open, isInSourcesRegion, cancelClose, scheduleClose]);

  const row = (
    <PopoverMenuItem
      ref={triggerRef}
      onMouseEnter={() => {
        cancelClose();
        onOpenChange(true);
      }}
      className={cn(ADD_MENU_ROW_CLASSNAME, 'items-center', open && 'bg-surface-hover')}
      aria-haspopup="true"
      aria-expanded={open}
      aria-label={label}
      data-submenu-open={open || undefined}
      data-testid="add-menu-sources-row"
      icon={
        <FramedIcon
          icon={<BookOpen aria-hidden />}
          size="sm"
          className={ADD_MENU_FRAMED_ICON_CLASSNAME}
        />
      }
      accessoryEnd={
        <ChevronRight className="size-4 shrink-0 text-text-secondary rtl:-scale-x-100" />
      }
    >
      {label}
    </PopoverMenuItem>
  );

  return (
    <Suspense fallback={row}>
      <SourcesPopover open={open} onOpenChange={onOpenChange}>
        {row}
      </SourcesPopover>
    </Suspense>
  );
};

interface SkillPickerListProps {
  skills: readonly WorkflowSkill[];
  onInvoke: (skill: WorkflowSkill) => void;
}

export const SkillPickerList = ({
  skills,
  onInvoke,
}: SkillPickerListProps): JSX.Element => {
  const intl = useIntl();

  return (
    <>
      {skills.map((skill) => (
        <ComposerMenuRow
          key={skill.skillId}
          icon={<Routines className="size-4 shrink-0 text-text-tertiary" aria-hidden />}
          title={skill.title}
          secondary={intl.formatMessage(
            { defaultMessage: '/{slug}', id: 'ryE0dUTlhd' },
            { slug: skill.slug }
          )}
          onClick={() => onInvoke(skill)}
          data-testid={`link-context-skill-${skill.slug}`}
        />
      ))}
    </>
  );
};

interface SpeedSubmenuProps {
  value: 'fast' | 'normal';
  onChange: (next: 'fast' | 'normal') => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Fast / Normal flyout. Two radios, no search — ContextSubmenu's Command
 * search box and fixed height are the wrong chrome for a pair of options.
 */
export const SpeedSubmenu = ({
  value,
  onChange,
  open,
  onOpenChange,
}: SpeedSubmenuProps): JSX.Element => {
  const intl = useIntl();
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const label = intl.formatMessage({
    defaultMessage: 'Speed',
    id: 'lhtNzGwBSY',
    description: 'Composer add-menu row that opens Fast or Normal inference speed',
  });
  const fastLabel = intl.formatMessage({
    defaultMessage: 'Fast',
    id: 'J4Cmj6qZWr',
    description: 'Work composer inference speed: Fast',
  });
  const normalLabel = intl.formatMessage({
    defaultMessage: 'Normal',
    id: '669KbubVd6',
    description: 'Work composer inference speed: Normal',
  });
  const linkedName = value === 'fast' ? fastLabel : normalLabel;

  const openSubmenu = useCallback(() => {
    onOpenChange(true);
  }, [onOpenChange]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <PopoverMenuItem
          ref={anchorRef}
          onMouseEnter={openSubmenu}
          onFocus={openSubmenu}
          onClick={openSubmenu}
          className={cn(ADD_MENU_ROW_CLASSNAME, open && 'bg-surface-hover')}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={label}
          data-submenu-open={open || undefined}
          data-testid="composer-speed-submenu"
          icon={
            <FramedIcon
              icon={<Gauge aria-hidden />}
              size="sm"
              className={ADD_MENU_FRAMED_ICON_CLASSNAME}
            />
          }
          description={linkedName}
          accessoryEnd={
            <ChevronRight className="size-4 shrink-0 text-text-secondary rtl:-scale-x-100" />
          }
        >
          {label}
        </PopoverMenuItem>
      </PopoverAnchor>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={4}
        // Same contract as ContextSubmenu (see PR 1 / AGE-2400): collisions must
        // stay off or Radix re-enables flip(), which teleports this pane across
        // the pointer near the right window edge. `size()` still publishes
        // --radix-popover-content-available-height with collisions disabled, so
        // the max-h clamp below shrinks the pane to fit without flipping.
        avoidCollisions={false}
        collisionPadding={SPEED_SUBMENU_COLLISION_PADDING}
        className="max-h-[var(--radix-popover-content-available-height)] w-44 overflow-hidden rounded-lg p-0 data-[state=closed]:animate-none data-[state=open]:animate-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        onMouseEnter={openSubmenu}
        data-testid="composer-speed-flyout"
      >
        {/*
          The pane's `max-h` clamp shrinks it to the viewport instead of flipping,
          which turns an overrun into a pane too short for its own content — so
          something inside has to absorb the shrink or the clamp just clips
          "Normal" away unreachably. ContextSubmenu gets that for free from
          `CommandList`; two radios need this scroller. Clamped to the same
          variable because the pane is `p-0`, so the two heights coincide.
          `scrollbar-subtle` per scrollbars.md decision tree point 2: a scroller
          inside an `overflow-hidden` rounded parent needs the flush thumb.
        */}
        <div className="scrollbar-subtle max-h-[var(--radix-popover-content-available-height)] overflow-y-auto">
          <div role="menu" className="flex flex-col p-1">
            {(['fast', 'normal'] as const).map((option) => {
              const selected = value === option;
              const optionLabel = option === 'fast' ? fastLabel : normalLabel;
              return (
                <button
                  key={option}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  onClick={() => onChange(option)}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-start hover:bg-surface-hover',
                    focusRing
                  )}
                  data-testid={`composer-speed-option-${option}`}
                >
                  <TypographyP2 className="min-w-0 flex-1 font-normal text-text-primary">
                    {optionLabel}
                  </TypographyP2>
                  {selected ? (
                    <Check size={14} className="shrink-0 text-text-primary" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SHORTCUTS_FLYOUT_COLLISION_PADDING = 8;
const SHORTCUTS_FLYOUT_SIDE_OFFSET = 4;
const SHORTCUTS_FLYOUT_PANE_WIDTH = 256;

/**
 * Shortcuts flyout. No search — the list is the Evidence menu, injected by
 * the host. Side is measured once per open so the pane does not flip under
 * the pointer when the in-session panel sits on the right.
 */
export const ShortcutsFlyout = ({
  open,
  onOpenChange,
  label,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  children: ReactNode;
}): JSX.Element => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [side, setSide] = useState<FlyoutSide>('right');

  useLayoutEffect(() => {
    if (!open) {
      setSide('right');
      return;
    }
    const anchor = anchorRef.current;
    if (!anchor) return;
    setSide(
      resolveFlyoutSide(
        anchor.getBoundingClientRect(),
        document.documentElement.clientWidth,
        {
          paneWidth: SHORTCUTS_FLYOUT_PANE_WIDTH,
          sideOffset: SHORTCUTS_FLYOUT_SIDE_OFFSET,
          collisionPadding: SHORTCUTS_FLYOUT_COLLISION_PADDING,
        }
      )
    );
  }, [open]);

  const openSubmenu = useCallback(() => {
    onOpenChange(true);
  }, [onOpenChange]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <PopoverMenuItem
          ref={anchorRef}
          onMouseEnter={openSubmenu}
          onFocus={openSubmenu}
          onClick={openSubmenu}
          className={cn(
            ADD_MENU_ROW_CLASSNAME,
            'items-center',
            open && 'bg-surface-hover'
          )}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={label}
          data-submenu-open={open || undefined}
          data-testid="composer-shortcuts-submenu"
          icon={
            <FramedIcon
              icon={<SlashSquare aria-hidden />}
              size="sm"
              className={ADD_MENU_FRAMED_ICON_CLASSNAME}
            />
          }
          accessoryEnd={
            <ChevronRight className="size-4 shrink-0 text-text-secondary rtl:-scale-x-100" />
          }
        >
          {label}
        </PopoverMenuItem>
      </PopoverAnchor>
      <PopoverContent
        side={side}
        align="start"
        sideOffset={SHORTCUTS_FLYOUT_SIDE_OFFSET}
        avoidCollisions={false}
        collisionPadding={SHORTCUTS_FLYOUT_COLLISION_PADDING}
        className="w-64 overflow-hidden rounded-lg p-0 data-[state=closed]:animate-none data-[state=open]:animate-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (anchorRef.current?.contains(e.target as Node)) e.preventDefault();
        }}
        onMouseEnter={openSubmenu}
        data-testid="composer-shortcuts-flyout"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};
